import { QuantativeList } from "../../DataManagment/Quantative";
import useScopeSummary from "../common/hooks/useEmission";

/* ── Map route prop → API scope key ── */
const SCOPE_LABEL = {
    SCOPE1: "scope 1",
    SCOPE2: "scope 2",
    SCOPE3: "scope 3",
};

const SCOPE_TITLE = {
    SCOPE1: "Scope 1 — Direct Emissions",
    SCOPE2: "Scope 2 — Indirect Energy Emissions",
    SCOPE3: "Scope 3 — Value Chain Emissions",
};

const SCOPE_COLOR = {
    SCOPE1: { accent: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
    SCOPE2: { accent: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    SCOPE3: { accent: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
};

/* ── K-only formatter: always divide by 1000, keep up to 3 decimal places ── */
/* e.g. 223,100,021,054 → "223,100,021.054 K"                                */
const formatK = (num) => {
  if (num === 0) return "0";
  if (num > 999) {
    const inK = num / 1000;
    return inK.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    }) + "K";
  }
    
};

/* ── Summary cards ── */
const ScopeSummaryCards = ({ scopeType, scopeData, loading, error }) => {
    const color = SCOPE_COLOR[scopeType] || SCOPE_COLOR.SCOPE1;
    const apiKey = SCOPE_LABEL[scopeType];
    const payload = scopeData?.[apiKey] || {};
    const total = Number(payload.total_emission || 0);
    const cats = payload.categories || {};
    const catEntries = Object.entries(cats);

    const gridCols = catEntries.length <= 2 ? 2 : 3;

    if (loading) {
        return (
            <div style={{ display: "flex", gap: 12 }}>
                {[1, 2, 3].map(i => (
                    <div key={i} style={{
                        flex: 1, height: 88, borderRadius: 10, background: "#f1f5f9",
                    }} />
                ))}
            </div>
        );
    }

    if (error || !scopeData) return null;

    return (
        <div style={{
            display: "flex",
            gap: 12,
            alignItems: "stretch",
            width: "100%",
            overflowX: "auto",
        }}>

            {/* ── Total emission card — shrink-wraps to number width ── */}
            <div style={{
                flexShrink: 0,
                display: "inline-flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "12px 18px",
                background: color.bg,
                border: `1.5px solid ${color.border}`,
                borderRadius: 10,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}>
                <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: color.accent,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontFamily: "var(--font-sans)",
                    marginBottom: 8,
                    whiteSpace: "nowrap",
                }}>
                    Total Emission
                </div>

                <div style={{ display: "flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap" }}>
                    <span style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: "var(--ink, #1e293b)",
                        fontFamily: "var(--font-mono, monospace)",
                        letterSpacing: "-0.03em",
                        lineHeight: 1,
                    }}>
                        {formatK(total)}
                    </span>
                    <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--ink-4, #64748b)",
                        letterSpacing: "0.04em",
                    }}>
                        kgCO₂e
                    </span>
                </div>
            </div>

            {/* ── Category cards grid ── */}
            {catEntries.length > 0 && (
                <div style={{
                    flex: 1,
                    display: "grid",
                    gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                    gap: 10,
                    minWidth: 0,
                }}>
                    {catEntries.map(([label, value]) => {
                        const num = Number(value);
                        const pct = total > 0 ? Math.min((num / total) * 100, 100) : 0;

                        return (
                            <div key={label} style={{
                                padding: "12px 14px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderTop: `3px solid ${color.accent}`,
                                borderRadius: 10,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                minWidth: 0,
                                overflow: "hidden",
                            }}>
                                {/* Label — wraps naturally, no truncation */}
                                <div style={{
                                    fontSize: 9.5,
                                    fontWeight: 700,
                                    color: "var(--ink-3, #64748b)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    fontFamily: "var(--font-sans)",
                                    marginBottom: 8,
                                    lineHeight: 1.4,
                                    wordBreak: "break-word",
                                }}>
                                    {label}
                                </div>

                                {/* Number + unit row */}
                                <div style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    flexWrap: "wrap",   // wraps unit to next line only if truly needed
                                    gap: "2px 4px",
                                }}>
                                    <span style={{
                                        fontSize: 18,
                                        fontWeight: 800,
                                        color: color.accent,
                                        fontFamily: "var(--font-mono, monospace)",
                                        letterSpacing: "-0.02em",
                                        lineHeight: 1,
                                        wordBreak: "break-all", // break number itself only as last resort
                                    }}>
                                        {formatK(num)}
                                    </span>
                                    <span style={{
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color: "var(--ink-4, #94a3b8)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                        fontFamily: "var(--font-mono, monospace)",
                                        whiteSpace: "nowrap",
                                    }}>
                                        kgCO₂e
                                    </span>
                                </div>

                                {/* Progress bar */}
                                <div style={{ marginTop: 10 }}>
                                    <div style={{
                                        height: 3,
                                        borderRadius: 2,
                                        background: "#f1f5f9",
                                        overflow: "hidden",
                                    }}>
                                        <div style={{
                                            height: "100%",
                                            borderRadius: 2,
                                            background: color.accent,
                                            width: `${pct}%`,
                                            transition: "width 0.5s ease",
                                        }} />
                                    </div>
                                    <div style={{
                                        marginTop: 3,
                                        fontSize: 9,
                                        color: "var(--ink-4, #94a3b8)",
                                        fontFamily: "var(--font-mono, monospace)",
                                        textAlign: "right",
                                    }}>
                                        {pct.toFixed(1)}%
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ── Main ── */
const MainScope = (props) => {
    const scopeType = props.scopeType || "SCOPE1";
    const { scopeData, loading, error } = useScopeSummary(SCOPE_LABEL[scopeType]);

    const summarySlot = (
        <ScopeSummaryCards
            scopeType={scopeType}
            scopeData={scopeData}
            loading={loading}
            error={error}
        />
    );

    return (
        <QuantativeList
            {...props}
            pageTitle={SCOPE_TITLE[scopeType]}
            emissionMode={true}
            scopeFilter={scopeType}
            summarySlot={summarySlot}
        />
    );
};

export default MainScope;